import React, { useMemo } from 'react';
import { twMerge } from 'tailwind-merge';
import { useAppSelector } from 'flow/hooks/useRedux';
import {
    selectOriginDTOSubtype,
    selectOriginVisitorId,
    selectVisitorDisplayName,
} from 'flow/selectors/TicketsSelectors';
import {
    selectExtensionKeyFromContactKey,
    selectUserCountryCode,
} from 'flow/selectors/ExtensionSelectors';
import { TicketKey } from 'flow/types/EntityKeys';
import { TypedMap } from 'flow/types/TypedMap';
import { ContactFull } from 'flow/types/Contact';
import useAppContext from 'flow/hooks/useAppContext';
import {
    originContactIsPhoneNumber,
    TICKET_ORIGIN,
} from 'flow/utils/ticket-channel-utils';
import CardCallDetailsContainer from 'flow/components/pbx/v2/misc/CardCallDetailsContainer';
import SectionTitle from 'flow/components/pbx/v2/detail-page/SectionTitle';
import ContactSettingsItems from 'flow/components/contact-card/ContactSettingsItems';
import { buildContactFields } from './buildContactFields';
import ContactDetailRow from './ContactDetailRow';
import ContactCardActionButton from './ContactCardActionButton';

interface Props {
    ticketKey: TicketKey;
    contact?: TypedMap<ContactFull>;
    contactIsSaved: boolean;
    inPanel: boolean;
}

/**
 * Card 2 — "Contact details": the edit / add-contact action, then for a saved
 * contact the shared / favourite / priority-call switches (the contact card's
 * own `ContactSettingsItems`, as the PBX side panel shows them), then every
 * populated contact field.
 */
const ContactDetailsCard = ({
    ticketKey,
    contact,
    contactIsSaved,
    inPanel,
}: Props) => {
    const { i18next } = useAppContext();
    const ownCountryCode = useAppSelector(selectUserCountryCode);

    const originVisitorId = useAppSelector((state) =>
        selectOriginVisitorId(state, ticketKey),
    );
    const visitorDisplayName = useAppSelector((state) =>
        selectVisitorDisplayName(state, ticketKey),
    );
    const originDtoSubtype = useAppSelector((state) =>
        selectOriginDTOSubtype(state, ticketKey),
    );
    const originIsEmail = originDtoSubtype === TICKET_ORIGIN.EMAIL;

    // The visitor id is a number to show and dial only on the channels where
    // it is one (call, queue, WhatsApp): an email origin's is an address, and
    // an unknown channel's is nothing this client can call.
    const visitorNumber = originContactIsPhoneNumber(originDtoSubtype)
        ? originVisitorId
        : undefined;
    const email =
        contact?.get('email') ||
        (originIsEmail ? visitorDisplayName : undefined);

    const fields = useMemo(
        () =>
            buildContactFields({
                contact,
                visitorNumber,
                email,
                ownCountryCode,
                t: i18next.t,
            }),
        [contact, visitorNumber, email, ownCountryCode, i18next],
    );

    // A colleague's switches go by their extension (favourited as the
    // extension, no priority switch on the user's own card).
    const contactKey = contact?.get('key');
    const colleagueKey = useAppSelector((state) =>
        contactKey ? selectExtensionKeyFromContactKey(state, contactKey) : null,
    );

    return (
        <CardCallDetailsContainer
            className={twMerge(
                'flex-col gap-2.5 rounded-xl pb-6',
                inPanel && 'bg-gray-200',
            )}
        >
            <div className="flex w-full items-center justify-between gap-2">
                <SectionTitle
                    title={i18next.t(
                        'tickets.contact.detailsTitle',
                        'Contact details',
                    )}
                    className="text-xl"
                />
                <ContactCardActionButton
                    contact={contact}
                    contactIsSaved={contactIsSaved}
                    inPanel={inPanel}
                />
            </div>

            {/* Switches first, as on the contact card elsewhere in the app.
             *  The rows drop their ContactBox inset so they line up with the
             *  fields below inside this card's own padding. */}
            {contact && contactIsSaved ? (
                <div className="mt-2 mb-2 flex w-full flex-col gap-2.5 border-b border-gray-300 pb-4">
                    <ContactSettingsItems
                        contact={contact}
                        extensionKey={colleagueKey ?? undefined}
                        rowClassName="mx-0 p-0"
                    />
                </div>
            ) : null}

            {fields.map((field) => (
                <ContactDetailRow
                    key={field.key}
                    field={field}
                    contact={contact}
                />
            ))}
        </CardCallDetailsContainer>
    );
};

export default React.memo(ContactDetailsCard);
